// ============================================================
// PlatformShell — left-rail layout for the Platform Control
// surface. Renders the section nav and an <Outlet/> for the
// active view. Distinct from the main shell sidebar so the
// operator/admin control plane is visually framed apart from
// the project-first journey, while still living *inside* the
// Square Code mainframe shell (top bar, command bar, OLIVIUM
// right-panel modes are all preserved).
// ============================================================

import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import type { CSSProperties } from 'react'
import { SHELL_TOKENS, text } from '../../shell/styles'

interface NavItem {
  key: string
  label: string
  to: string
  glyph: string
  /** If true, shown with a muted "planned" badge and lower visual priority. */
  planned?: boolean
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const NAV: NavGroup[] = [
  {
    label: 'WORKSPACE',
    items: [
      { key: 'projects',  label: 'Projects',  to: '/square/control/projects',  glyph: '◈' },
      { key: 'general',   label: 'General',   to: '/square/control/general',   glyph: '◇' },
    ],
  },
  {
    label: 'IDENTITY',
    items: [
      { key: 'security', label: 'Security', to: '/square/control/security', glyph: '◉' },
      { key: 'sso',      label: 'SSO',      to: '/square/control/sso',      glyph: '◎', planned: true },
    ],
  },
  {
    label: 'INTEGRATIONS',
    items: [
      { key: 'integrations', label: 'Integrations', to: '/square/control/integrations', glyph: '◐' },
      { key: 'oauth',        label: 'OAuth Apps',   to: '/square/control/oauth',        glyph: '◑', planned: true },
      { key: 'webhooks',     label: 'Webhooks',     to: '/square/control/webhooks',     glyph: '◒' },
      { key: 'api-keys',     label: 'API Keys',     to: '/square/control/api-keys',     glyph: '◓' },
    ],
  },
  {
    label: 'INFRASTRUCTURE',
    items: [
      { key: 'database',       label: 'Database',       to: '/square/control/database',       glyph: '▢' },
      { key: 'storage',        label: 'Storage',        to: '/square/control/storage',        glyph: '▣' },
      { key: 'realtime',       label: 'Realtime',       to: '/square/control/realtime',       glyph: '◬' },
      { key: 'infra',          label: 'Hosting & DNS',  to: '/square/control/infrastructure', glyph: '◇' },
    ],
  },
  {
    label: 'GOVERNANCE',
    items: [
      { key: 'audit',   label: 'Audit Logs', to: '/square/control/audit',   glyph: '◆' },
      { key: 'privacy', label: 'Privacy',    to: '/square/control/privacy', glyph: '◈' },
      { key: 'legal',   label: 'Legal',      to: '/square/control/legal',   glyph: '◉', planned: true },
    ],
  },
]

export default function PlatformShell() {
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '220px 1fr',
        gap: 18,
        maxWidth: 1180,
        margin: '0 auto',
      }}
    >
      {/* Left rail */}
      <aside
        style={{
          borderRight: `1px solid ${SHELL_TOKENS.borderAlt}`,
          paddingRight: 12,
        }}
      >
        <div
          style={{
            ...text.kicker,
            fontSize: 9,
            letterSpacing: '0.24em',
            color: SHELL_TOKENS.inkWhisper,
            marginBottom: 4,
            paddingLeft: 8,
          }}
        >
          SQUARE CODE
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: SHELL_TOKENS.brand,
            marginBottom: 18,
            paddingLeft: 8,
            fontFamily: 'var(--font-brand)',
            letterSpacing: '-0.005em',
          }}
        >
          Platform Control
        </div>

        {NAV.map((group, gi) => (
          <div key={group.label} style={{ marginBottom: gi === NAV.length - 1 ? 0 : 16 }}>
            <div
              style={{
                ...text.kicker,
                fontSize: 8,
                letterSpacing: '0.22em',
                color: SHELL_TOKENS.inkWhisper,
                marginBottom: 6,
                paddingLeft: 8,
              }}
            >
              {group.label}
            </div>
            {group.items.map((item) => {
              const active =
                location.pathname === item.to ||
                location.pathname.startsWith(item.to + '/')
              return (
                <RailButton
                  key={item.key}
                  active={active}
                  glyph={item.glyph}
                  label={item.label}
                  planned={item.planned}
                  onSelect={() => navigate(item.to)}
                />
              )
            })}
          </div>
        ))}
      </aside>

      {/* Active view */}
      <section style={{ minWidth: 0 }}>
        <Outlet />
      </section>
    </div>
  )
}

function RailButton({
  active,
  glyph,
  label,
  planned,
  onSelect,
}: {
  active: boolean
  glyph: string
  label: string
  planned?: boolean
  onSelect: () => void
}) {
  const style: CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textAlign: 'left',
    marginBottom: 2,
    padding: '7px 10px',
    borderRadius: 8,
    border: active ? `1px solid ${SHELL_TOKENS.brandLine}` : '1px solid transparent',
    background: active ? SHELL_TOKENS.brandSoft : 'transparent',
    color: active ? '#ffffff' : planned ? SHELL_TOKENS.inkFaint : SHELL_TOKENS.inkDim,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    fontFamily: 'inherit',
    transition: 'background 120ms, border-color 120ms',
    opacity: planned && !active ? 0.6 : 1,
  }

  return (
    <button type="button" onClick={onSelect} style={style}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: 5,
          fontSize: 10,
          color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.inkFaint,
          background: active ? 'rgba(124,58,237,0.10)' : 'rgba(255,255,255,0.02)',
          border: `1px solid ${active ? 'rgba(124,58,237,0.24)' : 'rgba(255,255,255,0.04)'}`,
          flexShrink: 0,
        }}
      >
        {glyph}
      </span>
      {label}
      {planned && (
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 8,
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.12em',
            color: SHELL_TOKENS.inkWhisper,
            border: `1px solid ${SHELL_TOKENS.border}`,
            borderRadius: 4,
            padding: '1px 5px',
          }}
        >
          PLANNED
        </span>
      )}
    </button>
  )
}
